import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { TimeEntry, CreateTimeEntryInput, UpdateTimeEntryInput } from '@merkos-mgmt/shared';

export function useTimeEntries(
  params?: { status?: string; start_date?: string; end_date?: string },
  options?: { enabled?: boolean },
) {
  const qp = new URLSearchParams();
  if (params?.status) qp.set('status', params.status);
  if (params?.start_date) qp.set('start_date', params.start_date);
  if (params?.end_date) qp.set('end_date', params.end_date);
  const qs = qp.toString();
  return useQuery({
    queryKey: ['mobile-time-entries', params],
    queryFn: () => api<{ rows: TimeEntry[]; total: number }>(`/time-entries${qs ? `?${qs}` : ''}`),
    enabled: options?.enabled,
  });
}

/** The running timer's query key — `TimerContext` reads and writes the entry through it. */
export const ACTIVE_TIMER_KEY = ['mobile-active-timer'] as const;

export function useActiveTimer() {
  return useQuery({
    queryKey: ACTIVE_TIMER_KEY,
    queryFn: () => api<TimeEntry | null>('/time-entries/active'),
    staleTime: 60_000,
    refetchInterval: 60_000,
    // Catch up the moment the app is back in front: a timer stopped on another
    // device must not keep counting here (backgrounded apps don't poll).
    refetchOnWindowFocus: 'always',
  });
}

export function useMyProjects() {
  return useQuery({
    queryKey: ['mobile-my-projects-time'],
    // `last_used` is the user's most recent entry on each project, so the picker
    // can surface most-recently-used first and collapse to the default count.
    queryFn: () => api<Array<{ id: string; name: string; group_name: string; department_name: string; last_used?: string | null }>>('/time-entries/my-projects'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useLastProject() {
  return useQuery({
    queryKey: ['mobile-last-project'],
    queryFn: () => api<{ project_id: string | null }>('/time-entries/last-project'),
    staleTime: 5 * 60 * 1000,
  });
}

/** App settings that the mobile timer needs — currently just the configurable
 *  default project count so the picker honors the same admin setting as web. */
export function useAppSettings() {
  return useQuery({
    queryKey: ['mobile-app-settings'],
    queryFn: () => api<{ time_default_project_count: number }>('/auth/app-settings'),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTimeEntryInput) =>
      api<TimeEntry>('/time-entries', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['mobile-time-entries'] }); qc.invalidateQueries({ queryKey: ['mobile-active-timer'] }); },
  });
}

export function useUpdateTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateTimeEntryInput }) =>
      api<TimeEntry>(`/time-entries/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['mobile-time-entries'] }); },
  });
}

export function useStopTimer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes?: string }) =>
      api<TimeEntry>(`/time-entries/${id}/stop`, { method: 'POST', body: JSON.stringify({ notes }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['mobile-time-entries'] }); qc.invalidateQueries({ queryKey: ['mobile-active-timer'] }); },
  });
}

export function useSubmitTimeEntries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api<{ submitted: number }>('/time-entries/submit', { method: 'POST', body: JSON.stringify({ ids }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['mobile-time-entries'] }); },
  });
}

export function useApproveTimeEntries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) =>
      api<{ approved: number }>('/time-entries/approve', { method: 'POST', body: JSON.stringify({ ids }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['mobile-time-entries'] }); },
  });
}

export function useRejectTimeEntries() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, notes }: { ids: string[]; notes?: string }) =>
      api<{ rejected: number }>('/time-entries/reject', { method: 'POST', body: JSON.stringify({ ids, notes }) }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['mobile-time-entries'] }); },
  });
}

export function useDeleteTimeEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/time-entries/${id}`, { method: 'DELETE' }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['mobile-time-entries'] }); },
  });
}
